import { Environment, useEnvironment } from "@amzn/xb-hooks";
import merge from "lodash/merge";
import { createContext, useContext, useMemo } from "react";
import { fallbackConfig } from "../config/fallbackConfig";
import { ApolloClientProviderConfig } from "../providers";
import { EvaluateFeatureProviderConfig } from "../providers/EvaluateFeatureHookProvider";
import { LocaleProviderConfig } from "../providers/LocalizationHookProvider/LocaleProvider";
import { LiveContextProviderConfig } from "../providers/Tier0LiveContextProvider";
import { AdditionalCookieConfig } from "./useCookieRegistry";
import { ShortbreadConfig } from "./useShortbreadDomain";

/**
 * Each module that uses config should export their own Config type and add it here as part of {@link Config}.
 * Additionally, they will need to extend {@link fallbackConfig} to include a fallback value that is either a sensible
 * default or satisfies the Null Object Pattern ({@see https://en.wikipedia.org/wiki/Null_object_pattern} for more details).
 */
export interface Config
  extends EvaluateFeatureProviderConfig,
    ApolloClientProviderConfig,
    LocaleProviderConfig,
    AdditionalCookieConfig,
    LiveContextProviderConfig,
    ShortbreadConfig {}

export type XBProviderConfig = Partial<Record<"default" | Environment, Partial<Config>>>;

export const ConfigContext = createContext<XBProviderConfig>({});

/**
 * Resolve the config file for the current request. Each field is resolved independently using the
 * rules of @link https://lodash.com/docs/4.17.15#merge.
 *
 * The priority of the config sources are as follows:
 *  1. Environment specific config provided to the @see ExperienceBridgeProvider
 *  2. "default" config provided to the @see ExperienceBridgeProvider
 *  3. @see fallbackConfig
 *
 * @returns {Config} the resolved config
 */
export const useConfig: () => Config = () => {
  const allConfigs = useContext(ConfigContext);

  const { environment } = useEnvironment();
  return useMemo(
    () => merge({}, fallbackConfig, allConfigs.default, allConfigs?.[environment]),
    [allConfigs, environment],
  );
};
